/**
 * Where every seat sits, what it has in front of it, and what it is doing.
 *
 * Kept out of the component because it is arithmetic, not markup: it has to reconcile
 * against the pot the chart states, and that is a thing to test rather than to eyeball.
 */
/**
 * Only the four fields this needs, declared here rather than imported.
 *
 * `api.ts` reads `import.meta.env`, which is Vite's and does not exist outside the
 * browser build — importing its types dragged that into the test runner. Structural
 * typing means a real ChartNode satisfies this without either side knowing about the
 * other.
 */
export interface TableNode {
  /** e.g. "root/BTN", or "root/UTG:shove/MP" when facing a shove. */
  path: string;
  /** The seat to act. */
  actor: string;
  potBb: number;
  stacksBb: Record<string, number>;
}

/** Preflop action order. The last two seats are always the blinds. */
const SEAT_ORDER: Record<number, string[]> = {
  2: ["SB", "BB"],
  3: ["BTN", "SB", "BB"],
  6: ["UTG", "MP", "CO", "BTN", "SB", "BB"],
  9: ["UTG", "UTG1", "UTG2", "MP", "MP1", "CO", "BTN", "SB", "BB"],
};

type SeatState = "hero" | "allin" | "folded" | "waiting";

export interface Seat {
  name: string;
  stack: number;
  posted: number;
  state: SeatState;
  x: number;
  y: number;
  /**
   * PHASE 9: the seat's bearing from the centre, in radians (the hero at pi/2, the rest
   * clockwise). `x`/`y` are this bearing on the landscape oval; the kit's spot table
   * also draws a portrait one, on the same bearing, when its column is phone-narrow.
   */
  angle: number;
}

export function seatsFor(node: TableNode): { seats: Seat[]; heroIdx: number } {
  const names = SEAT_ORDER[Object.keys(node.stacksBb).length]
    ?? Object.keys(node.stacksBb);
  const n = names.length;
  const heroIdx = names.indexOf(node.actor);

  // "root/UTG:shove/MP" — the seat before ":shove" put everyone all in.
  const jammer = node.path.includes(":shove/")
    ? names.indexOf(node.path.slice(node.path.indexOf("/") + 1, node.path.indexOf(":shove/")))
    : -1;

  /**
   * Recovering the ante from the pot.
   *
   * On an opening node the pot is just the posted chips, so the ante is what is left
   * after the two blinds. On a node facing a shove it is NOT: `emit-multiway` writes
   * the pot as the posted chips less the jammer's own posting plus the jammer's whole
   * stack, so dividing by the seats gave 2.33 a head at a table whose real ante is
   * 0.12 — every seat drawn with roughly twenty times the chips it had actually put in.
   *
   * Undoing that exactly: pot = n*ante + 1.5 - jammerPosted + jammerStack.
   */
  const blindOf = (i: number) => (i === n - 2 ? 0.5 : i === n - 1 ? 1 : 0);
  const ante = jammer < 0
    ? Math.max(0, (node.potBb - 1.5) / n)
    : Math.max(0, (node.potBb - 1.5 + blindOf(jammer) - (node.stacksBb[names[jammer]!] ?? 0)) / (n - 1));

  const seats = names.map((name, i): Seat => {
    const posted = ante + blindOf(i);
    const state: SeatState =
      i === heroIdx ? "hero"
      : i === jammer ? "allin"
      : i < heroIdx ? "folded"        // acted before the hero and is not the jammer
      : "waiting";

    /**
     * Hero sits at the bottom, the way every poker client puts you, and the rest run
     * clockwise from there — so the seats drawn to your left really are the ones
     * still to act.
     */
    const offset = (i - heroIdx + n) % n;
    const angle = (Math.PI / 2) + (offset * 2 * Math.PI) / n;
    /**
     * A seat that is all in has nothing behind: its chips are in the pot, and drawing
     * them as a stack shows the same money twice.
     */
    const full = node.stacksBb[name] ?? 0;
    return {
      name,
      stack: state === "allin" ? 0 : full,
      posted: state === "allin" ? full : posted,
      state,
      x: 50 + 40 * Math.cos(angle),
      y: 50 + 36 * Math.sin(angle),
      angle,
    };
  });

  return { seats, heroIdx };
}

